/**
 * Money in IECN DELIVERY OS is ALWAYS integer minor units (US cents).
 * Floating point never touches a price, a driver payout or an invoice line.
 * See docs/PRICING_ENGINE.md.
 */
export const DEFAULT_CURRENCY = 'USD';
export class MoneyError extends Error {
}
/** Assert a value is a safe integer amount of cents. Throws rather than silently corrupting money. */
export function assertCents(value, label = 'amount') {
    if (typeof value !== 'number' || !Number.isInteger(value) || !Number.isSafeInteger(value)) {
        throw new MoneyError(`${label} must be an integer number of cents, received: ${String(value)}`);
    }
}
/** Convert a human dollar figure to cents. Only for config/import boundaries — never mid-calculation. */
export function dollarsToCents(dollars) {
    if (typeof dollars !== 'number' || !Number.isFinite(dollars)) {
        throw new MoneyError(`cannot convert ${String(dollars)} to cents`);
    }
    return Math.round(dollars * 100);
}
export function centsToDollars(cents) {
    assertCents(cents);
    return cents / 100;
}
export function addCents(...amounts) {
    let total = 0;
    for (const amount of amounts) {
        assertCents(amount);
        total += amount;
    }
    return total;
}
export function subtractCents(a, b) {
    assertCents(a);
    assertCents(b);
    return a - b;
}
/**
 * Multiply cents by a rate (e.g. a 1.5x weekend multiplier or a 0.65 driver pay share).
 * Rounds half away from zero so a 1.5x on an odd cent never silently loses money.
 */
export function multiplyCents(amount, factor) {
    assertCents(amount);
    if (!Number.isFinite(factor))
        throw new MoneyError(`invalid factor ${String(factor)}`);
    const raw = amount * factor;
    return raw < 0 ? -Math.round(Math.abs(raw)) : Math.round(raw);
}
/** Percentage helper: percentOf(10000, 8.75) === 875 */
export function percentOf(amount, percent) {
    return multiplyCents(amount, percent / 100);
}
export function maxCents(a, b) {
    assertCents(a);
    assertCents(b);
    return a > b ? a : b;
}
export function clampMin(amount, minimum) {
    return maxCents(amount, minimum);
}
/** Format for display. Customer-facing strings must always come through here. */
export function formatMoney(cents, currency = DEFAULT_CURRENCY, locale = 'en-US') {
    assertCents(cents);
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
    }).format(cents / 100);
}
/**
 * Gross margin as a percentage, to one decimal place.
 * Returns 0 when revenue is 0 rather than NaN/Infinity — dashboards must never render NaN.
 */
export function marginPercent(revenue, cost) {
    assertCents(revenue);
    assertCents(cost);
    if (revenue === 0)
        return 0;
    return Math.round(((revenue - cost) / revenue) * 1000) / 10;
}
//# sourceMappingURL=money.js.map